/**
 * 作品内容（整本 JSON）的纯函数：克隆、按路径读写、新条目 id、素材 tag 与反查。
 * 编辑器的工作副本按不可变方式更新：writePath 只复制从根到改动点的对象与数组，其余分支与旧副本共享，
 * 所以保存时当前引用就是发送快照，脏检查（composables/novel/same-json.js）遇到共享分支直接判相同。
 */

export function cloneJson(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value))
}

/** 按路径读值，路径上任一层缺失都返回 undefined。 */
export function readPath(root, path) {
  let node = root
  for (const key of path) {
    if (node === null || node === undefined) return undefined
    node = node[key]
  }
  return node
}

/** 按路径写值，返回新的根；只复制路径上的对象与数组，未改动的分支原样共享。 */
export function writePath(root, path, value) {
  if (path.length === 0) return value
  const [key, ...rest] = path
  const node = Array.isArray(root) ? [...root] : { ...root }
  node[key] = writePath(root?.[key], rest, value)
  return node
}

/** Collision-resistant entity ids are independent of draft order and concurrent editors. */
export function uniqueId(prefix, list = []) {
  const taken = new Set(list.map(item => item?.id))
  let candidate
  do {
    const bytes = new Uint8Array(12)
    globalThis.crypto.getRandomValues(bytes)
    candidate = `${prefix}-${Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')}`
  } while (taken.has(candidate))
  return candidate
}

/** 素材 tag：`<kind>-<实体标识>`，与 contracts 的 NOVEL_ASSET_TAG_PATTERN 同形（小写字母数字、下划线、连字符）。 */
export function assetTag(kind, ...parts) {
  const suffix = parts.join('-').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80)
  return suffix ? `${kind}-${suffix}` : kind
}

/** 物品图的素材 tag（素材种类 item，C19）。 */
export function itemImageTag(item_id) {
  return assetTag('item', item_id)
}

const KIND_PURPOSE = Object.freeze({ cover: 'cover', location: 'location', state_image: 'character', item: 'item' })

/**
 * 素材属于哪个条目、做什么用（C-2）：从对象键 `novel/packs/<pack>/assets/<tag>/<hash>.<ext>` 取 tag，
 * 与各条目按上传时同一规则算出的 tag 比对。对不上现有条目（条目已删或 id 改过）时只按种类给用途。
 * @returns {{ purpose: 'cover'|'portrait'|'state'|'item'|'location'|'character', name: string|null }}
 */
export function assetOwner(pack, storage_key) {
  const tag = /\/assets\/([^/]+)\/[^/]+$/.exec(storage_key)?.[1] ?? ''
  for (const person of pack?.characters ?? []) {
    const person_name = person.name || person.id
    if (tag === assetTag('state_image', person.id)) return { purpose: 'portrait', name: person_name }
    for (const state of person.performance_states ?? []) {
      if (tag === assetTag('state_image', person.id, state.id)) return { purpose: 'state', name: `${person_name} · ${state.id}` }
    }
  }
  for (const item of pack?.items ?? []) {
    if (tag === itemImageTag(item.id)) return { purpose: 'item', name: item.name || item.id }
  }
  for (const place of pack?.locations ?? []) {
    if (tag === assetTag('location', place.id)) return { purpose: 'location', name: place.name || place.id }
  }
  return { purpose: KIND_PURPOSE[tag.split('-')[0]] ?? 'character', name: null }
}

/** 数字输入框给的是字符串：非空转成数字；清空时保留空串，交给严格校验报错（同 `v-model.number`）。 */
export function numberValue(value) {
  return value === '' ? '' : Number(value)
}

/** Web Crypto's random values also work on local HTTP preview hosts. */
export function createRequestId() {
  if (globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID()
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
